import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';

import { useAuth } from '../context/AuthContext';
import { authService } from '../services/authService';
import { billingApi } from '../services/api';
import { PasswordInput } from '../components/PasswordInput';
import LegalFooter from '../components/LegalFooter';

const VALID_PLANS = ['starter', 'growth', 'pro'];

const RESEND_COOLDOWN_SECONDS = 60; // mirrors backend OTP_RESEND_COOLDOWN_SECONDS

type Step = 'form' | 'otp';

const Signup: React.FC = () => {
  const { applyToken } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  // Plan carried from the public pricing page (/signup?plan=growth) → resume to
  // Checkout after verification. Validated against the known tiers.
  const planParam = params.get('plan');
  const pendingPlan = planParam && VALID_PLANS.includes(planParam) ? planParam : null;

  const [step, setStep] = useState<Step>('form');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [agreed, setAgreed] = useState(false);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const apiError = (err: any, fallback: string) => {
    const detail = err?.response?.data?.detail;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail) && detail[0]?.msg) return detail[0].msg;
    return fallback;
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!agreed) {
      setError('Please agree to the Terms & Conditions and Privacy Policy to continue.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      await authService.signup({ username: username.trim(), email: email.trim(), password });
      setStep('otp');
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(apiError(err, 'Sign up failed. Please try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await authService.verifyOtp(email.trim().toLowerCase(), code.trim());
      applyToken(result);
      // Resume the pricing funnel: take the new user straight to Stripe Checkout
      // for the tier they picked. Fall back to Home if checkout can't start.
      if (pendingPlan) {
        try {
          const url = await billingApi.createCheckout(pendingPlan);
          window.location.assign(url);
          return;
        } catch {
          navigate('/pricing?status=cancelled', { replace: true });
          return;
        }
      }
      navigate('/', { replace: true });
    } catch (err: any) {
      setError(apiError(err, 'Verification failed. Check the code and try again.'));
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    if (cooldown > 0) return;
    setError(null);
    try {
      await authService.resendOtp(email.trim().toLowerCase());
      setCooldown(RESEND_COOLDOWN_SECONDS);
    } catch (err: any) {
      setError(apiError(err, 'Could not resend the code yet.'));
    }
  };

  return (
    <div style={wrapStyle}>
      <div style={cardStyle}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <img src={`${process.env.PUBLIC_URL}/icon.png`} alt="Dial Sniper" style={{ height: 56, marginBottom: 12 }} />
          <h1 style={titleStyle}>Dial Sniper</h1>
          <p style={subtitleStyle}>{step === 'form' ? 'Create Account' : 'Verify Email'}</p>
        </div>

        {error && <div style={errorStyle}>{error}</div>}

        {step === 'form' ? (
          <form onSubmit={handleSignup}>
            <Field label="Username">
              <input type="text" value={username} onChange={(e) => setUsername(e.target.value)}
                autoComplete="username" required minLength={3} style={inputStyle} />
            </Field>
            <Field label="Email">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                autoComplete="email" required style={inputStyle} />
            </Field>
            <Field label="Password" hint="At least 8 characters" marginBottom={16}>
              <PasswordInput value={password} onChange={setPassword}
                autoComplete="new-password" required minLength={8} style={inputStyle} />
            </Field>
            <label style={agreeRowStyle}>
              <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)}
                style={checkboxStyle} />
              <span style={agreeTextStyle}>
                I agree to the{' '}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" style={agreeLinkStyle}>
                  Terms &amp; Conditions
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" style={agreeLinkStyle}>
                  Privacy Policy
                </Link>.
              </span>
            </label>
            <button type="submit" disabled={loading || !agreed} className="sidebar-new-run-btn" style={{ width: '100%' }}>
              {loading ? 'Sending code…' : 'Sign Up'}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerify}>
            <p style={helpTextStyle}>
              We sent a 6-digit code to <strong style={{ color: 'var(--text)' }}>{email}</strong>. Enter it below.
            </p>
            <Field label="Verification Code" marginBottom={20}>
              <input type="text" inputMode="numeric" pattern="[0-9]*" maxLength={6}
                value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                autoFocus required style={{ ...inputStyle, letterSpacing: '0.4em', textAlign: 'center', fontSize: 18 }} />
            </Field>
            <button type="submit" disabled={loading || code.length < 6} className="sidebar-new-run-btn" style={{ width: '100%' }}>
              {loading ? 'Verifying…' : 'Verify & Continue'}
            </button>
            <button type="button" onClick={handleResend} disabled={cooldown > 0} style={linkButtonStyle}>
              {cooldown > 0 ? `Resend code in ${cooldown}s` : 'Resend code'}
            </button>
          </form>
        )}

        <div style={{ textAlign: 'center', marginTop: 18 }}>
          <Link to="/login" style={linkStyle}>Back to sign in</Link>
        </div>

        <LegalFooter />
      </div>
    </div>
  );
};

const Field: React.FC<{ label: string; hint?: string; marginBottom?: number; children: React.ReactNode }> = ({
  label, hint, marginBottom = 12, children,
}) => (
  <label style={{ display: 'block', marginBottom }}>
    <div style={labelStyle}>{label}</div>
    {children}
    {hint && <div style={{ marginTop: 4, color: 'var(--text-faint)', fontSize: 11 }}>{hint}</div>}
  </label>
);

const wrapStyle: React.CSSProperties = {
  minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--bg)', padding: 24,
};
const cardStyle: React.CSSProperties = {
  width: '100%', maxWidth: 380, background: 'var(--surface)', border: '2px solid var(--border)',
  boxShadow: '8px 8px 0 rgba(0,0,0,0.6)', padding: '36px 36px',
};
const titleStyle: React.CSSProperties = {
  margin: 0, color: 'var(--amber)', fontFamily: 'var(--font-display)', letterSpacing: '0.06em',
};
const subtitleStyle: React.CSSProperties = {
  margin: '8px 0 0', color: 'var(--text-faint)', fontFamily: 'var(--font-data)', fontSize: 12,
  letterSpacing: '0.14em', textTransform: 'uppercase',
};
const labelStyle: React.CSSProperties = {
  marginBottom: 6, color: 'var(--text-soft)', fontSize: 12, textTransform: 'uppercase',
};
const errorStyle: React.CSSProperties = {
  marginBottom: 16, padding: '10px 12px', border: '1px solid var(--category-dead-fg)',
  background: 'var(--category-dead-bg)', color: 'var(--category-dead-fg)', fontSize: 13,
};
const helpTextStyle: React.CSSProperties = {
  margin: '0 0 16px', color: 'var(--text-soft)', fontSize: 13, lineHeight: 1.5,
};
const inputStyle: React.CSSProperties = {
  width: '100%', padding: '10px 12px', background: 'var(--surface-subtle)', color: 'var(--text)',
  border: '1px solid var(--border)', outline: 'none',
};
const linkButtonStyle: React.CSSProperties = {
  width: '100%', marginTop: 10, background: 'none', border: 'none', color: 'var(--text-faint)',
  fontSize: 12, cursor: 'pointer', textTransform: 'uppercase', letterSpacing: '0.08em',
};
const linkStyle: React.CSSProperties = {
  color: 'var(--text-faint)', fontSize: 12, textDecoration: 'none', letterSpacing: '0.04em',
};
const agreeRowStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'flex-start', gap: 9, marginBottom: 18, cursor: 'pointer',
};
const checkboxStyle: React.CSSProperties = {
  marginTop: 2, width: 15, height: 15, accentColor: 'var(--amber)', flex: '0 0 auto', cursor: 'pointer',
};
const agreeTextStyle: React.CSSProperties = {
  color: 'var(--text-soft)', fontSize: 12, lineHeight: 1.45,
};
const agreeLinkStyle: React.CSSProperties = {
  color: 'var(--amber)', textDecoration: 'underline',
};

export default Signup;
